import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Search } from "lucide-react";
import Layout from "./layout";
import { campaignData } from "../data/mockData";
import CampaignManagementModal from "../Modal/CampaignManagementModal";
import EditCampaignManagementModal from "../Modal/EditCampaignManagementModal";
import { campaignAPI } from "../services/api";

const PageContainer = styled.div`
  background-color: #ffffff;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-right: 3rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SearchButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #333333;
  }
`;

const AddButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #333333;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: none;
  background-color: ${(props) => (props.active ? "#1976D2" : "transparent")};
  color: ${(props) => (props.active ? "white" : "#1976D2")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  font-size: 0.875rem;
  min-width: 40px;
  text-decoration: ${(props) => (props.active ? "none" : "underline")};

  &:hover:not(:disabled) {
    background-color: ${(props) => (props.active ? "#1565C0" : "#E3F2FD")};
  }

  &:disabled {
    color: #9e9e9e;
    text-decoration: none;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: ${(props) => props.align || "center"};
  font-size: 16px;
  font-weight: 400;
  color: #a3a3a3;
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 14px;
  color: #6f6f6f;
  text-align: ${(props) => props.align || "center"};
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
`;

const TextButton = styled.button`
  background: none;
  border: none;
  color: #404e67;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const Campaign = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    fetchCampaigns(currentPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  useEffect(() => {
    loadAllCampaigns();
  }, []);

  const mapCampaign = (campaign) => {
    const formatDate = (dateStr) => {
      if (!dateStr || dateStr === "0000-00-00" || dateStr === "00/00/0000") {
        return "-";
      }

      try {
        if (dateStr.includes("-")) {
          const [year, month, day] = dateStr.split("-");
          return `${day}/${month}/${year}`;
        }
        return dateStr;
      } catch (e) {
        return dateStr;
      }
    };

    return {
      ...campaign,
      id: campaign.id,
      code: campaign.id,
      company: campaign.companyname || "-",
      name: campaign.name || "-",
      duration: campaign.day ? `${campaign.day} วัน` : "-",
      cost: campaign.price ? `${campaign.price} บาท` : "ฟรี",
      promoCode: campaign.promocode || "-",
      startDate: formatDate(campaign.datestart),
      endDate: formatDate(campaign.datestop),
    };
  };

  const loadAllCampaigns = async () => {
    try {
      const firstRes = await campaignAPI.getList(1);
      if (firstRes.result && Array.isArray(firstRes.list)) {
        let allPages = [...firstRes.list];
        const pageAmount = parseInt(firstRes.pageAmount) || 1;

        if (pageAmount > 1) {
          const pageResponses = await Promise.all(
            Array.from({ length: pageAmount - 1 }, (_, index) =>
              campaignAPI.getList(index + 2)
            )
          );

          pageResponses.forEach((pageResponse) => {
            if (pageResponse.result && Array.isArray(pageResponse.list)) {
              allPages = allPages.concat(pageResponse.list);
            }
          });
        }

        const uniqueCampaigns = allPages
          .filter((campaign) => campaign.name && campaign.name !== "-")
          .map(mapCampaign)
          .filter(
            (campaign, index, self) =>
              index === self.findIndex((c) => c.name === campaign.name)
          );

        setAllCampaigns(uniqueCampaigns);
      }
    } catch (err) {
      console.error("Error loading all campaigns:", err);
    }
  };

  const fetchCampaigns = async (page = 1, keyword = "") => {
    setLoading(true);
    setError(null);
    try {
      const response = await campaignAPI.getList(page);

      console.log("🔍 fetchCampaigns - Raw API Response:", response);

      if (response.result && response.list && Array.isArray(response.list)) {
        const mappedCampaigns = response.list.map((campaign) => {
          const formatDate = (dateStr) => {
            if (
              !dateStr ||
              dateStr === "0000-00-00" ||
              dateStr === "00/00/0000"
            )
              return "-";
            try {
              if (dateStr.includes("-")) {
                const [year, month, day] = dateStr.split("-");
                return `${day}/${month}/${year}`;
              }
              return dateStr;
            } catch (e) {
              return dateStr;
            }
          };

          return {
            ...campaign,
            id: campaign.id,
            code: campaign.id,
            company: campaign.companyname || "-",
            name: campaign.name || "-",
            duration: campaign.day ? `${campaign.day} วัน` : "-",
            cost: campaign.price ? `${campaign.price} บาท` : "ฟรี",
            promoCode: campaign.promocode || "-",
            startDate: formatDate(campaign.datestart),
            endDate: formatDate(campaign.datestop),
          };
        });
        setCampaigns(mappedCampaigns);
        setTotalPages(parseInt(response.pageAmount) || 1);
        if (page === 1 && mappedCampaigns.length > 0) setPageSize(mappedCampaigns.length);
      } else if (response.data && Array.isArray(response.data)) {
        setCampaigns(response.data);
      } else if (Array.isArray(response)) {
        setCampaigns(response);
      } else {
        setCampaigns(campaignData);
      }
    } catch (err) {
      console.error("Error fetching campaigns:", err);
      setError(err.message);
      setCampaigns(campaignData);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);

    if (searchTerm === "") {
      setCampaigns(allCampaigns);
    } else {
      const filtered = allCampaigns.filter(
        (campaign) => campaign.name === searchTerm,
      );
      setCampaigns(filtered);
    }
  };

  const handleAddCampaign = async (campaignData) => {
    try {
      await campaignAPI.add(campaignData);
      fetchCampaigns(currentPage);
      loadAllCampaigns();
      setIsModalOpen(false);
    } catch (err) {
      console.error("Error adding campaign:", err);
      setError("เกิดข้อผิดพลาดในการเพิ่มบริษัท: " + err.message);
    }
  };

  const handleUpdateCampaign = async (campaignData) => {
    try {
      const updateResponse = await campaignAPI.update(campaignData);
      console.log("✅ Campaign updated successfully:", updateResponse);

      // รอสักครู่ให้ backend commit ข้อมูล
      await new Promise((resolve) => setTimeout(resolve, 500));

      await fetchCampaigns(currentPage);
      loadAllCampaigns();
      setIsEditModalOpen(false);
      setSelectedCampaign(null);
    } catch (err) {
      console.error("Error updating campaign:", err);
      setError("เกิดข้อผิดพลาดในการแก้ไขบริษัท: " + err.message);
    }
  };

  const handleEditClick = (campaign) => {
    setSelectedCampaign(campaign);
    setIsEditModalOpen(true);
  };

  const currentData = campaigns || [];

  return (
    <Layout>
      <PageContainer>
        {loading && (
          <div style={{ textAlign: "center", padding: "1rem" }}>
            กำลังโหลดข้อมูล...
          </div>
        )}
        {error && (
          <div
            style={{ textAlign: "center", padding: "1rem", color: "#EF4444" }}
          >
            เกิดข้อผิดพลาด: {error}
          </div>
        )}

        <HeaderSection>
          <SearchContainer>
            <select
              style={{
                width: "100%",
                padding: "0.75rem 1rem",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
                backgroundColor: "white",
                cursor: "pointer",
              }}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value === "") {
                  fetchCampaigns(currentPage);
                } else {
                  const filtered = allCampaigns.filter(
                    (campaign) => campaign.name === e.target.value,
                  );
                  setCampaigns(filtered);
                }
              }}
            >
              <option value="">ทั้งหมด</option>
              {allCampaigns
                .filter((campaign) => campaign.name && campaign.name !== "-")
                .map((campaign) => (
                  <option key={campaign.id} value={campaign.name}>
                    {campaign.name}
                  </option>
                ))}
            </select>
          </SearchContainer>

          <AddButton onClick={() => setIsModalOpen(true)}>
            เพิ่มบริษัท
          </AddButton>
        </HeaderSection>

        <PaginationContainer>
          <PaginationButton
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            previous
          </PaginationButton>
          {[...Array(totalPages)].map((_, index) => (
            <PaginationButton
              key={index + 1}
              active={currentPage === index + 1}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </PaginationButton>
          ))}
          <PaginationButton
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            disabled={currentPage === totalPages}
          >
            next
          </PaginationButton>
        </PaginationContainer>

        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>ลำดับ</TableHeader>
                <TableHeader>รหัสบริษัท</TableHeader>
                <TableHeader>บริษัท</TableHeader>
                <TableHeader>แคมเปญส่งเสริมสุขภาพ</TableHeader>
                <TableHeader></TableHeader>
              </tr>
            </TableHead>
            <tbody>
              {currentData.map((campaign, index) => (
                <TableRow key={campaign.id}>
                  <TableCell>{(currentPage - 1) * pageSize + index + 1}</TableCell>
                  <TableCell>{campaign.code}</TableCell>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>{campaign.caption || "-"}</TableCell>
                  <TableCell>
                    <ActionButtonsContainer>
                      <TextButton onClick={() => handleEditClick(campaign)}>
                        แก้ไข
                      </TextButton>
                    </ActionButtonsContainer>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </PageContainer>

      <CampaignManagementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddCampaign}
        allCampaigns={allCampaigns}
      />

      <EditCampaignManagementModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCampaign(null);
        }}
        onSave={handleUpdateCampaign}
        campaignData={selectedCampaign}
        allCampaigns={allCampaigns}
      />
    </Layout>
  );
};

export default Campaign;
